import React, { useState, useCallback, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { Localization, AppMessagesContext, SessionContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow, closeWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton } from "src/components/GlassButtons";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import { BsChevronLeft, BsChevronRight, BsZoomIn, BsZoomOut, BsCheck2, BsMap, BsDownload, BsCheckCircle } from "react-icons/bs";
import type { LLMAnalysisResult, LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import { cutPdfToSelectedPages } from "./ANNPDFCutter";
import { renderPdfPageToImage } from "./ANNPDFPageToImage";
import { uploadOverlayImage, deriveOverlayPlacement, blobToDataUrl } from "./ANNImageOverlayStorage";
import { ANNManager } from "./ANNManager";
import { AnnotationType, AnnotationContextType, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { useANNEditorContext, ANNEditorActionType } from "./ANNEditorState";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ANNPDFViewerProps {
   managedWindow: ManagedWindow;
}

interface WindowParams {
   pdfUrl: string;
   pdfBlob?: Blob;
   analysisResult?: LLMAnalysisResult;
   aktId?: string;
   documentId?: string;
   contextType?: AnnotationContextType;
   organizationId?: string;
   userId?: string;
   userName?: string;
   datoloebenr?: string;
   documentFilename?: string;
}

function SimpleButton({
   children,
   onClick,
   disabled,
   selected,
   variant,
   title,
   className
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   selected?: boolean;
   variant?: 'default' | 'success' | 'primary' | 'muted';
   title?: string;
   className?: string;
}) {
   const variantClass =
      variant === "success" ? "ann-pdf-btn--success" :
         variant === "primary" ? "ann-pdf-btn--primary" :
            variant === "muted" ? "ann-pdf-btn--muted" : "";
   const selectedClass = selected ? "ann-pdf-btn--selected" : "";
   const classes = ["ann-pdf-btn", variantClass, selectedClass, className].filter(Boolean).join(" ");

   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className={classes}
      >
         {children}
      </button>
   );
}

function PageButton({
   pageNum,
   isSelected,
   isAISuggested,
   isCurrent,
   onClick
}: {
   pageNum: number;
   isSelected: boolean;
   isAISuggested: boolean;
   isCurrent: boolean;
   onClick: () => void;
}) {
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");
   const classes = [
      "ann-pdf-pageBtn",
      isSelected ? "ann-pdf-pageBtn--selected" : "",
      isCurrent ? "ann-pdf-pageBtn--current" : ""
   ].filter(Boolean).join(" ");
   return (
      <button
         onClick={onClick}
         className={classes}
         title={isAISuggested ? `${aiSuggestedLabel} - Page ${pageNum}` : `Page ${pageNum}`}
      >
         {pageNum}
      </button>
   );
}

function ControlButton({
   children,
   onClick,
   disabled,
   title
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   title?: string;
}) {
   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className="ann-pdf-controlBtn"
      >
         {children}
      </button>
   );
}

function SaveDocumentModal({
   open,
   onClose,
   onSave,
   saving,
   canVerify,
   defaultTitle,
   defaultHistoryComment
}: {
   open: boolean;
   onClose: () => void;
   onSave: (annotationType: AnnotationTypeCategory, quality: AnnotationQuality | undefined, verifyStamp: boolean, title: string, historyComment: string) => void;
   saving: boolean;
   canVerify: boolean;
   defaultTitle?: string;
   defaultHistoryComment?: string;
}) {
   const [annotationType, setAnnotationType] = useState<AnnotationTypeCategory>("Dokument");
   const [quality, setQuality] = useState<AnnotationQuality | undefined>(undefined);
   const [verifyStamp, setVerifyStamp] = useState(false);
   const [title, setTitle] = useState(defaultTitle || "");
   const [historyComment, setHistoryComment] = useState(defaultHistoryComment || "");

   useEffect(() => {
      if (defaultTitle) setTitle(defaultTitle);
      if (defaultHistoryComment) setHistoryComment(defaultHistoryComment);
   }, [defaultTitle, defaultHistoryComment]);

   if (!open) return null;

   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={Localization.getText("Save Document Annotation")}
         allowFullscreen={false}
         allowUserMiniMize={false}
         PosDefault={{ left: "30%", top: "20%", width: "480px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <div className="ann-create-form-container">
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Annotation Type")}
                  </label>
                  <select
                     value={annotationType}
                     onChange={e => setAnnotationType(e.target.value as AnnotationTypeCategory)}
                     className="ann-create-select"
                  >
                     {ANNOTATION_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                     ))}
                  </select>
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Quality Assessment")}
                  </label>
                  <div className="ann-create-quality-selector-group">
                     <div
                        onClick={() => setQuality(undefined)}
                        className={`ann-create-quality-selector ${quality === undefined ? "ann-create-quality-selector-none ann-create-quality-selector-selected" : ""}`}
                     >
                        <span className="ann-create-quality-label">{Localization.getText("None")}</span>
                     </div>
                     {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                        const isSelectedQ = quality === q;
                        const label = q === AnnotationQuality.Gold ? Localization.getText("Gold")
                           : q === AnnotationQuality.Silver ? Localization.getText("Silver")
                              : Localization.getText("Bronze");
                        return (
                           <div
                              key={q}
                              onClick={() => setQuality(q)}
                              className={`ann-create-quality-selector ${isSelectedQ ? "ann-create-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                           >
                              <div className={`ann-create-quality-indicator-circle ${isSelectedQ ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                              <span className={`ann-create-quality-label ${isSelectedQ ? "ann-create-quality-label-selected" : ""}`}>{label}</span>
                           </div>
                        );
                     })}
                  </div>
               </div>

               {canVerify && (
                  <div>
                     <label
                        className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                     >
                        <input
                           type="checkbox"
                           checked={verifyStamp}
                           onChange={(e) => setVerifyStamp(e.target.checked)}
                           className="ann-create-verifyCheckbox"
                        />
                        <BsCheckCircle
                           size={20}
                           className="ann-create-verifyIcon"
                        />
                        <span className="ann-create-verifyText">
                           {Localization.getText("Stamp this annotation as verified")}
                        </span>
                     </label>
                  </div>
               )}

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Title")}
                  </label>
                  <input
                     value={title}
                     onChange={(e) => setTitle(e.target.value)}
                     className="ann-create-input"
                  />
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("History comment")}
                  </label>
                  <textarea
                     rows={3}
                     value={historyComment}
                     onChange={(e) => setHistoryComment(e.target.value)}
                     className="ann-create-textarea"
                     placeholder={Localization.getText("Optional history comment")}
                  />
               </div>

               <div className="ann-create-button-group">
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
                  <GlassButton disabled={saving || !title.trim()} onClick={() => onSave(annotationType, quality, verifyStamp, title, historyComment)}>
                     {saving ? Localization.getText("Saving...") : Localization.getText("Save")}
                  </GlassButton>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}

export function ANNPDFViewer({ managedWindow }: ANNPDFViewerProps) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: mapitStateDispatch, state: mapitState } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: editorDispatch } = useANNEditorContext();
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const params = managedWindow.windowParams as WindowParams | undefined;

   const [numPages, setNumPages] = useState<number>(0);
   const [currentPage, setCurrentPage] = useState<number>(1);
   const [scale, setScale] = useState<number>(1.0);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);

   const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
   const [saving, setSaving] = useState<boolean>(false);
   const [uploadingPage, setUploadingPage] = useState<number | null>(null);
   const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
   const displayRef = useRef<HTMLDivElement | null>(null);
   const autoPageChangeTimestampRef = useRef<number>(0);
   const lastDisplayScrollTopRef = useRef<number>(0);
   const lastDisplayScrollMoveAtRef = useRef<number>(Date.now());
   const bottomScrollArmAtRef = useRef<number | null>(null);
   const topScrollArmAtRef = useRef<number | null>(null);

   const analysisItem: LLMAnalysisResultItem | undefined = params?.analysisResult?.items?.[0];
   const pagesKept = analysisItem?.pages_kept || [];

   useEffect(() => {
      if (pagesKept.length > 0) {
         setSelectedPages(new Set(pagesKept));
      }
   }, [pagesKept.join(',')]);

   useEffect(() => {
      if (params?.pdfBlob) {
         const reader = new FileReader();
         reader.onload = () => {
            setPdfData(reader.result);
         };
         reader.onerror = () => {
            setError("Failed to read PDF data");
         };
         reader.readAsArrayBuffer(params.pdfBlob);
      } else if (params?.pdfUrl) {
         setPdfData(params.pdfUrl);
      }
   }, [params?.pdfBlob, params?.pdfUrl]);

   const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setLoading(false);
      if (pagesKept.length > 0) {
         setCurrentPage(pagesKept[0]);
      }
   }, [pagesKept]);

   const onDocumentLoadError = useCallback((error: Error) => {
      setError(`Failed to load PDF: ${error.message}`);
      setLoading(false);
   }, []);

   const goToPrevPage = useCallback(() => {
      setCurrentPage(prev => Math.max(1, prev - 1));
   }, []);

   const goToNextPage = useCallback(() => {
      setCurrentPage(prev => Math.min(numPages, prev + 1));
   }, [numPages]);

   const zoomIn = useCallback(() => {
      setScale(prev => Math.min(3, prev + 0.25));
   }, []);

   const zoomOut = useCallback(() => {
      setScale(prev => Math.max(0.5, prev - 0.25));
   }, []);

   const isAISuggestedPage = (page: number): boolean => {
      return pagesKept.includes(page);
   };

   const isSelectedPage = (page: number): boolean => {
      return selectedPages.has(page);
   };

   const togglePageSelection = useCallback((page: number) => {
      setSelectedPages(prev => {
         const newSet = new Set(prev);
         if (newSet.has(page)) {
            newSet.delete(page);
         } else {
            newSet.add(page);
         }
         return newSet;
      });
   }, []);

   const selectAllPages = useCallback(() => {
      const allPages = new Set<number>();
      for (let i = 1; i <= numPages; i++) {
         allPages.add(i);
      }
      setSelectedPages(allPages);
   }, [numPages]);

   const deselectAllPages = useCallback(() => {
      setSelectedPages(new Set());
   }, []);

   const resetToAISuggestions = useCallback(() => {
      setSelectedPages(new Set(pagesKept));
   }, [pagesKept]);

   const handleSaveDocument = useCallback(async (
      annotationType: AnnotationTypeCategory,
      quality: AnnotationQuality | undefined,
      verifyStamp: boolean,
      title: string,
      historyComment: string
   ) => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      const organizationId = params.organizationId || sessionState?.customerRef;
      const userId = params.userId || sessionState?.userRef;
      const userName = params.userName || sessionState?.userName;

      if (!organizationId || !userId || !userName) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }

      if (!params.documentId || !params.contextType) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Missing context for saving annotation")));
         return;
      }
      if (!title.trim()) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Title is required")));
         return;
      }

      setSaving(true);
      try {
         const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
         const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob, pagesToKeep);
         const filename = `${params.datoloebenr || params.aktId || 'document'}_cut_${pagesToKeep.length}pages.pdf`;
         const file = new File([cutPdfBlob], filename, { type: 'application/pdf' });
         const uploadResult = await uploadOverlayImage(file);

         await ANNManager.saveAnnotation({
            type: AnnotationType.Document,
            documentId: params.documentId,
            contextType: params.contextType,
            organizationId,
            userId,
            userName,
            datoloebenr: params.datoloebenr,
            document: {
               filename,
               s3Key: uploadResult.key,
               s3Url: uploadResult.url,
               mimeType: 'application/pdf',
               size: cutPdfBlob.size,
            },
            title: title.trim(),
            historyComment: historyComment.trim() || `Extracted pages: ${pagesToKeep.join(', ')}`,
            annotationType,
            quality,
            verified: canVerify && verifyStamp,
         });

         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Document saved successfully")
         ));

         setShowSaveModal(false);
         editorDispatch({ type: ANNEditorActionType.SaveSuccess });
         await new Promise(resolve => setTimeout(resolve, 1000));
         windowDispatch(closeWindow(managedWindow.id));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to save document")
         ));
      } finally {
         setSaving(false);
      }
   }, [params, selectedPages, sessionState, appMessageDispatch, canVerify, editorDispatch, windowDispatch, managedWindow.id]);

   const handleUploadToMap = useCallback(async (pageNumber: number) => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      setUploadingPage(pageNumber);
      try {
         const imageBlob = await renderPdfPageToImage(params.pdfBlob, pageNumber, 2.0);
         const filename = `${params.aktId || 'page'}_page${pageNumber}.png`;
         const file = new File([imageBlob], filename, { type: 'image/png' });
         await uploadOverlayImage(file);
         const dataUrl = await blobToDataUrl(imageBlob);
         const { coordinates } = await deriveOverlayPlacement({ blob: imageBlob }, mapitState.map?.getMapPop?.());
         const layerName = `${params.aktId || 'PDF'} - Page ${pageNumber}`;
         const newLayer = LayerFunc.createLayerInfoForImageOverlay(
            layerName,
            filename,
            dataUrl,
            coordinates
         );

         mapitStateDispatch(actionAddDataLayer(newLayer));

         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Page uploaded to map")
         ));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to upload page to map")
         ));
      } finally {
         setUploadingPage(null);
      }
   }, [params, mapitState.map, mapitStateDispatch, appMessageDispatch]);

   const handleDownloadCutPdf = useCallback(async () => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      setSaving(true);
      try {
         const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
         const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob, pagesToKeep);
         const filename = `${params.datoloebenr || params.aktId || 'document'}_pages_${pagesToKeep.join('-')}.pdf`;
         const url = URL.createObjectURL(cutPdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to download PDF")
         ));
      } finally {
         setSaving(false);
      }
   }, [params, selectedPages, appMessageDispatch]);

   const handleClose = useCallback(() => {
      windowDispatch(closeWindow(managedWindow.id));
   }, [windowDispatch, managedWindow.id]);

   useEffect(() => {
      if (displayRef.current) {
         displayRef.current.scrollTop = 0;
      }
      lastDisplayScrollTopRef.current = 0;
      lastDisplayScrollMoveAtRef.current = Date.now();
      bottomScrollArmAtRef.current = null;
      topScrollArmAtRef.current = null;
   }, [currentPage]);

   const handleDisplayScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
      const displayElement = event.currentTarget;
      const currentTop = displayElement.scrollTop;
      if (currentTop !== lastDisplayScrollTopRef.current) {
         lastDisplayScrollTopRef.current = currentTop;
         lastDisplayScrollMoveAtRef.current = Date.now();
      }
      const isAtBottom = currentTop + displayElement.clientHeight >= displayElement.scrollHeight - 2;
      if (!isAtBottom) {
         bottomScrollArmAtRef.current = null;
      }
      const isAtTop = currentTop <= 2;
      if (!isAtTop) {
         topScrollArmAtRef.current = null;
      }
   }, []);

   const handleDisplayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
      const displayElement = event.currentTarget;
      const now = Date.now();
      const msSinceLastScrollMove = now - lastDisplayScrollMoveAtRef.current;
      if (event.deltaY > 0 && currentPage < numPages) {
         const isAtBottom = displayElement.scrollTop + displayElement.clientHeight >= displayElement.scrollHeight - 2;
         if (!isAtBottom) {
            return;
         }

         // Require two deliberate wheel actions at the bottom to avoid accidental page flips from momentum scroll.
         if (msSinceLastScrollMove < 350) {
            bottomScrollArmAtRef.current = null;
            return;
         }

         const armAt = bottomScrollArmAtRef.current;
         if (!armAt || now - armAt > 1800) {
            bottomScrollArmAtRef.current = now;
            return;
         }
         if (now - armAt < 220) {
            return;
         }

         if (now - autoPageChangeTimestampRef.current > 400) {
            event.preventDefault();
            autoPageChangeTimestampRef.current = now;
            bottomScrollArmAtRef.current = null;
            setCurrentPage(prev => Math.min(numPages, prev + 1));
         }
      } else if (event.deltaY < 0 && currentPage > 1) {
         const isAtTop = displayElement.scrollTop <= 2;
         if (!isAtTop) {
            return;
         }

         // Mirror the same two-step logic when scrolling up at the top.
         if (msSinceLastScrollMove < 350) {
            topScrollArmAtRef.current = null;
            return;
         }

         const armAt = topScrollArmAtRef.current;
         if (!armAt || now - armAt > 1800) {
            topScrollArmAtRef.current = now;
            return;
         }
         if (now - armAt < 220) {
            return;
         }

         if (now - autoPageChangeTimestampRef.current > 400) {
            event.preventDefault();
            autoPageChangeTimestampRef.current = now;
            topScrollArmAtRef.current = null;
            setCurrentPage(prev => Math.max(1, prev - 1));
         }
      }
   }, [currentPage, numPages]);

   if (!params || (!params.pdfUrl && !params.pdfBlob)) {
      return (
         <AdvancedDragModal
            window={managedWindow}
            title={Localization.getText("PDF Analyzer")}
            onClose={handleClose}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  <div className="ann-pdf-noData">
                     {Localization.getText("No PDF data available")}
                  </div>
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   const canSave = params.documentId && params.contextType && selectedPages.size > 0;
   const confidenceDisplay = analysisItem?.confidence
      ? (analysisItem.confidence > 1 ? analysisItem.confidence : Math.round(analysisItem.confidence * 10))
      : null;

   const defaultHistoryComment = analysisItem?.summary
      ? `${analysisItem.summary.substring(0, 200)}${analysisItem.summary.length > 200 ? '...' : ''}`
      : '';
   const defaultTitle = params?.documentFilename || params?.datoloebenr || params?.aktId || Localization.getText("Document");
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");

   const titleParts: string[] = [Localization.getText("PDF Analyzer")];
   if (params.documentFilename) {
      titleParts.push(params.documentFilename);
   }
   if (params.datoloebenr) {
      titleParts.push(params.datoloebenr);
   } else if (params.aktId) {
      titleParts.push(params.aktId);
   }
   const windowTitle = titleParts.join(" - ");

   return (
      <AdvancedDragModal
         window={managedWindow}
         title={windowTitle}
         onClose={handleClose}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
      >
         <ADModalBody>
            <div className="ann-pdf-viewer">
               <div className="ann-pdf-columns">
                  <div className="ann-pdf-leftColumn">
                     <div className="ann-pdf-panel">
                        <div className="ann-pdf-pageSelectionHeader">
                           <strong>{Localization.getText("Page Selection")}:</strong>
                           <SimpleButton onClick={selectAllPages}>{Localization.getText("All")}</SimpleButton>
                           <SimpleButton onClick={deselectAllPages}>{Localization.getText("None")}</SimpleButton>
                           {pagesKept.length > 0 && (
                              <SimpleButton onClick={resetToAISuggestions}>{Localization.getText("AI")}</SimpleButton>
                           )}
                           <span className="ann-pdf-pageSelectionCount">
                              {selectedPages.size} / {numPages} {Localization.getText("pages")}
                           </span>
                        </div>
                        <div className="ann-pdf-pageSelectionButtons">
                           {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
                              <PageButton
                                 key={pageNum}
                                 pageNum={pageNum}
                                 isSelected={isSelectedPage(pageNum)}
                                 isAISuggested={isAISuggestedPage(pageNum)}
                                 isCurrent={currentPage === pageNum}
                                 onClick={() => {
                                    setCurrentPage(pageNum);
                                 }}
                              />
                           ))}
                        </div>
                        <div className="ann-pdf-pageSelectionActions">
                           <SimpleButton
                              onClick={handleDownloadCutPdf}
                              disabled={saving || selectedPages.size === 0}
                              variant="default"
                           >
                              <BsDownload />
                              {Localization.getText("Download Selected")}
                           </SimpleButton>

                           <SimpleButton
                              onClick={() => setShowSaveModal(true)}
                              disabled={saving || !canSave}
                              variant="default"
                           >
                              <BsCheck2 />
                              {Localization.getText("Save as Annotation")}
                           </SimpleButton>
                        </div>
                     </div>

                     {analysisItem && (
                        <div className="ann-pdf-panel">
                           <div className="ann-pdf-analysisLeft">
                              <strong>{Localization.getText("Summary")}:</strong>
                              <p className="ann-pdf-analysisSummaryText">{analysisItem.summary || "-"}</p>
                           </div>
                           <div className="ann-pdf-analysisMeta">
                              <div><strong>{Localization.getText("Confidence")}:</strong> {confidenceDisplay !== null ? `${confidenceDisplay}/10` : "-"}</div>
                              <div><strong>{aiSuggestedLabel}:</strong> {pagesKept.length} {Localization.getText("pages")}</div>
                           </div>
                           {analysisItem.reasoning && (
                              <details className="ann-pdf-analysisDetails">
                                 <summary><strong>{Localization.getText("Reasoning")}</strong></summary>
                                 <p>{analysisItem.reasoning}</p>
                              </details>
                           )}
                        </div>
                     )}
                  </div>

                  <div className="ann-pdf-rightColumn">
                     <div className="ann-pdf-toolbar">
                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={zoomOut}
                              title={Localization.getText("Zoom out")}
                           >
                              <BsZoomOut />
                           </ControlButton>
                           <span className="ann-pdf-toolbarValue">{Math.round(scale * 100)}%</span>
                           <ControlButton
                              onClick={zoomIn}
                              title={Localization.getText("Zoom in")}
                           >
                              <BsZoomIn />
                           </ControlButton>
                        </div>

                        <div className="ann-pdf-divider" />

                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={goToPrevPage}
                              disabled={currentPage <= 1}
                              title={Localization.getText("Previous page")}
                           >
                              <BsChevronLeft />
                           </ControlButton>
                           <span className="ann-pdf-toolbarPageValue">
                              {currentPage} / {numPages}
                           </span>
                           <ControlButton
                              onClick={goToNextPage}
                              disabled={currentPage >= numPages}
                              title={Localization.getText("Next page")}
                           >
                              <BsChevronRight />
                           </ControlButton>
                        </div>

                        <div className="ann-pdf-divider ann-pdf-divider--wide" />

                        <SimpleButton
                           onClick={() => togglePageSelection(currentPage)}
                           variant={isSelectedPage(currentPage) ? 'success' : 'default'}
                        >
                           <BsCheck2 />
                           {isSelectedPage(currentPage)
                              ? Localization.getText("Selected")
                              : Localization.getText("Select page")}
                        </SimpleButton>

                        <SimpleButton
                           onClick={() => handleUploadToMap(currentPage)}
                           disabled={uploadingPage === currentPage}
                        >
                           {uploadingPage === currentPage
                              ? <Spinner animation="border" size="sm" className="ann-pdf-miniSpinner" />
                              : <BsMap />}
                           {Localization.getText("Upload to Map")}
                        </SimpleButton>
                     </div>

                     <div className="ann-pdf-display" ref={displayRef} onScroll={handleDisplayScroll} onWheel={handleDisplayWheel}>
                        {loading && (
                           <div className="ann-pdf-loading">
                              <Spinner animation="border" role="status" />
                              <div className="ann-pdf-loadingText">{Localization.getText("Loading")}...</div>
                           </div>
                        )}

                        {error && (
                           <div className="ann-pdf-displayError">
                              {error}
                           </div>
                        )}

                        {pdfData && (
                           <div
                              className={`ann-pdf-pageFrame ${isAISuggestedPage(currentPage) && isSelectedPage(currentPage)
                                 ? "ann-pdf-pageFrame--suggestedSelected"
                                 : isSelectedPage(currentPage)
                                    ? "ann-pdf-pageFrame--selected"
                                    : isAISuggestedPage(currentPage)
                                       ? "ann-pdf-pageFrame--suggested"
                                       : "ann-pdf-pageFrame--none"
                                 }`}
                           >
                              {isAISuggestedPage(currentPage) && (
                                 <div className={`ann-pdf-suggestedBadge ${isSelectedPage(currentPage) ? "ann-pdf-suggestedBadge--selected" : ""}`}>
                                    {aiSuggestedLabel}
                                 </div>
                              )}
                              <Document
                                 file={pdfData}
                                 onLoadSuccess={onDocumentLoadSuccess}
                                 onLoadError={onDocumentLoadError}
                                 loading={null}
                              >
                                 <Page
                                    pageNumber={currentPage}
                                    scale={scale}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                 />
                              </Document>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            <SaveDocumentModal
               open={showSaveModal}
               onClose={() => setShowSaveModal(false)}
               onSave={handleSaveDocument}
               saving={saving}
               canVerify={canVerify}
               defaultTitle={defaultTitle}
               defaultHistoryComment={defaultHistoryComment}
            />
         </ADModalBody>
      </AdvancedDragModal>
   );
}
